import { MessageCircleIcon, MailIcon, PhoneIcon, MapPinIcon, ArrowRightIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { ContactVisit, VISIT_ID } from './ContactVisit';
import { LocalIntro } from './LocalIntro';
import { founder, salon } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { telHref } from '../../core/rules/contact';
import { useOverlayActions } from '../../contexts/StoreContext';
import { fill } from '../../utils/copy';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';
import { heroTitle } from '../../ui/sections';
import type { ContactChannel } from '../../core/types/pages';
type Action = {
  kind: 'link';
  href: string;
  external: boolean;
  srSuffix?: string;
} | {
  kind: 'button';
  onClick: () => void;
};
const ACTION_CLS = 'group inline-flex min-h-[44px] items-center gap-2 whitespace-nowrap text-[15px] text-ink underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay hover:underline';

/**
 * Contact, gebouwd rond vijf bezoekers:
 * 1. "Ik heb een vraag": WhatsApp bovenaan, zoals Sarah echt werkt.
 * 2. "Waar zit ze?": het adres staat al in de hero, met een sprong naar kaart en route.
 * 3. "Ik kom voor het eerst": onder "Langskomen" kaart, route, OV, parkeren, aanbellen en tijden.
 * 4. "Ik ben te laat": een eigen regel met de snelste weg (WhatsApp) en de verzetregel.
 * 5. "Mijn bestelling": mail met het onderwerp al ingevuld.
 * Adres, telefoon en openingstijden komen uit de instellingen; alleen de woorden uit Paginateksten.
 */
export function Contact() {
  const pages = usePages();
  const {
    policy
  } = useSite();
  const {
    openBooking
  } = useOverlayActions();
  const copy = pages.contact;
  const orderMail = `mailto:${salon.email}?subject=${encodeURIComponent('Vraag over mijn bestelling')}`;
  const whatsapp: Action = {
    kind: 'link',
    href: salon.whatsapp,
    external: true,
    srSuffix: shopLabels.viaWhatsApp
  };
  const channels: {
    key: string;
    channel: ContactChannel | undefined;
    action: Action;
  }[] = [{
    key: 'skin',
    channel: copy.channels.skin,
    action: whatsapp
  }, {
    key: 'booking',
    channel: copy.channels.booking,
    action: {
      kind: 'button',
      onClick: () => openBooking()
    }
  }, {
    key: 'advice',
    channel: copy.channels.advice,
    action: {
      kind: 'button',
      onClick: () => openBooking(policy.consult.slug)
    }
  }, {
    key: 'order',
    channel: copy.channels.order,
    action: {
      kind: 'link',
      href: orderMail,
      external: false
    }
  },
  // Oudere paginateksten uit de backend kennen dit kanaal nog niet: dan blijft het weg.
  {
    key: 'late',
    channel: copy.channels.late,
    action: whatsapp
  }];
  const visible = channels.filter((c): c is typeof c & {
    channel: ContactChannel;
  } => Boolean(c.channel?.title));
  return <>
      <section className="bg-sand" data-id="element-7170" data-mp="1ova7of9hypi0">
        <div className="mx-auto grid max-w-content gap-14 px-6 pb-20 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-28 lg:pt-20" data-id="element-7171" data-mp="1ou8lpfqy0ili">
          <div className="lg:col-span-5" data-id="element-7172" data-mp="1ma2g73mokk2u">
            {copy.hero.eyebrow && <p className="text-[14px] text-clay" data-id="element-7173" data-mp="1m9ay8vm0akv1">{fill(copy.hero.eyebrow)}</p>}
            <h1 className={`mt-4 lg:mt-5 ${heroTitle}`} data-id="element-7174" data-mp="1hcar0hdf7c2c">
              <HeadingText heading={copy.hero.heading} accentBlock data-id="element-7175" data-mp="1cbmpv2vh9eue" />
            </h1>
            {copy.hero.body && <p className="mt-7 max-w-md text-[17px] leading-relaxed text-muted" data-id="element-7176" data-mp="18akyt9bps93i">{fill(copy.hero.body)}</p>}

            {(copy.hero.cta || copy.hero.secondaryCta) && <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-7177" data-mp="13db3jd1n937r">
                {copy.hero.cta && <a href={salon.whatsapp} target="_blank" rel="noopener noreferrer" className={btnPrimaryLg} data-id="element-7178" data-mp="tleuj6y0rsor">
                    <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7179" data-mp="omu2vvwu2hbx" />
                    {fill(copy.hero.cta)}
                    <span className="sr-only" data-id="element-7180" data-mp="kk76rit4qa8x">{shopLabels.viaWhatsApp}</span>
                  </a>}
                {copy.hero.secondaryCta && salon.email && <a href={`mailto:${salon.email}`} className={btnSecondaryLg} data-id="element-7181" data-mp="2foy538anxjg6">
                    <MailIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7182" data-mp="2jq07fxzvmbmd" />
                    {fill(copy.hero.secondaryCta)}
                  </a>}
              </div>}

            {/* Wie het liever direct ziet: nummer, mail en adres als tekst, kopieerbaar. */}
            <dl className="mt-10 space-y-3 border-t border-ink/15 pt-6 text-[15px]" data-id="element-7183" data-mp="1vze7ku00zxng">
              {salon.phone && <div className="flex gap-3" data-id="element-7184" data-mp="21022hqrdkymy">
                  <dt className="sr-only" data-id="element-7185" data-mp="20wmkog6bhbhu">Telefoon en WhatsApp</dt>
                  <PhoneIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7186" data-mp="1n0sl4nrfnyhe" />
                  <dd data-id="element-7187" data-mp="1rykme8dywwy9">
                    <a href={telHref(salon.phoneIntl)} className="text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7188" data-mp="3hd8y07eqiav8">
                      <span className="sr-only" data-id="element-7189" data-mp="3mclhci6eczt2">Bel </span>
                      {salon.phone}
                    </a>
                    <span className="text-muted" data-id="element-7190" data-mp="bnzai91ig8n"> · ook WhatsApp</span>
                  </dd>
                </div>}
              {salon.email && <div className="flex gap-3" data-id="element-7191" data-mp="93pvg4c2tad4">
                  <dt className="sr-only" data-id="element-7192" data-mp="94rasl04kj0n">E-mail</dt>
                  <MailIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7193" data-mp="4by3pqh6oyle" />
                  <dd className="min-w-0" data-id="element-7194" data-mp="j0krmgkvmyo8">
                    <a href={`mailto:${salon.email}`} className="break-all text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7195" data-mp="izj8dotpj5vf">
                      {salon.email}
                    </a>
                  </dd>
                </div>}
              {salon.street && <div className="flex gap-3" data-id="element-7196" data-mp="o18fwuhq11er">
                  <dt className="sr-only" data-id="element-7197" data-mp="2tsvp8rru1rnk">Adres van de salon</dt>
                  <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7198" data-mp="2tvhzx0a0sna0" />
                  <dd data-id="element-7199" data-mp="sr7dth9ow9nt">
                    <span className="text-ink" data-id="element-7200" data-mp="srpkz8xw48ai">
                      {salon.street}, {salon.locality}
                    </span>
                    <a href={`#${VISIT_ID}`} className="ml-2 whitespace-nowrap text-[14px] text-muted underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink" data-id="element-7201" data-mp="xr271rgxtbc1">
                      Route en kaart
                    </a>
                  </dd>
                </div>}
            </dl>
            {salon.englishNote && <p className="mt-4 text-[13px] leading-relaxed text-muted" data-id="element-7202" data-mp="1gnbqkualyu3f">{salon.englishNote}.</p>}
          </div>

          {visible.length > 0 && <div className="lg:col-span-7 lg:pt-2" data-id="element-7203" data-mp="1qfhg7xde0zfg">
              <div className="flex items-center gap-4" data-id="element-7204" data-mp="1qe6c1wg15pnt">
                {/* Decoratief: haar naam staat in de tekst ernaast. */}
                <img src={founder.portrait} alt="" width={56} height={56} className="h-14 w-14 shrink-0 rounded-full object-cover object-top" data-id="element-7205" data-mp="1zfmeni0lsnhh" />
                <h2 className="font-display text-[1.9rem] font-light leading-snug text-ink sm:text-[2.2rem]" data-id="element-7206" data-mp="3jyge6mw3otv3">{fill(copy.channelsTitle)}</h2>
              </div>
              <ul className="mt-8 divide-y divide-ink/15 border-y border-ink/15" data-id="element-7207" data-mp="3awqnjja1lyyd">
                {visible.map(({
              key,
              channel,
              action
            }) => <li key={key} className="grid gap-3 py-7 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-8" data-id="element-7208" data-mp="35z76ycpvha39">
                    <div data-id="element-7209" data-mp="7fgtr724q3aq">
                      <h3 className="font-display text-[1.4rem] font-light leading-snug text-ink" data-id="element-7210" data-mp="7i33ndchsuw4">{fill(channel.title)}</h3>
                      {channel.body && <p className="mt-1.5 max-w-md text-[15px] leading-relaxed text-muted" data-id="element-7211" data-mp="3tut5xf834xn7">{fill(channel.body)}</p>}
                    </div>
                    {channel.cta && <ChannelAction action={action} label={fill(channel.cta)} data-id="element-7212" data-mp="3ouxa439jkciq" />}
                  </li>)}
              </ul>
            </div>}
        </div>
      </section>

      <ContactVisit data-id="element-7213" data-mp="le82sf24wys0" />

      <LocalIntro data-id="element-7214" data-mp="3py465eo39m9o" />
    </>;
}

/** De actie bij een soort vraag: een rustige tekstlink, zodat de hero-knoppen de hoofdactie blijven. */
function ChannelAction({
  action,
  label
}: {
  action: Action;
  label: string;
}) {
  const content = <>
      {label}
      <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7215" data-mp="3gv335muisba8" />
    </>;
  if (action.kind === 'button') {
    return <button type="button" onClick={action.onClick} aria-haspopup="dialog" className={`${ACTION_CLS} justify-self-start py-1`} data-id="element-7216" data-mp="ch501ave93nb">
        {content}
      </button>;
  }
  return <a href={action.href} {...action.external ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {}} className={`${ACTION_CLS} justify-self-start py-1`} data-id="element-7217" data-mp="3i02o7vkjgcq">
      {content}
      {action.srSuffix && <span className="sr-only" data-id="element-7218" data-mp="2oav3hr9ltnwx">{action.srSuffix}</span>}
    </a>;
}